const COUNTRY_CODE = String(process.env.REACT_APP_COUNTRY_CODE || 'AR').toUpperCase();

const CUSTOMER_USER_TYPE = 'cliente';
const OFFICIAL_STORE_USER_TYPE = 'tienda-oficial';

const CUSTOMER_IDENTITY_FIELD_BY_COUNTRY = {
  AR: 'dni',
  BR: 'cpf',
};

const OFFICIAL_STORE_IDENTITY_FIELD_BY_COUNTRY = {
  AR: 'cuit',
  BR: 'cnpj',
};

export const normalizeIdentityValue = value => String(value ?? '').replace(/\D/g, '');
export const normalizeAlphaNumericValue = value =>
  String(value ?? '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();

export const normalizeOfficialStoreIdentityValue = (value, countryCode = COUNTRY_CODE) => {
  return countryCode === 'BR' ? normalizeAlphaNumericValue(value) : normalizeIdentityValue(value);
};

export const getActiveCountryCode = () => COUNTRY_CODE;

const isValidArgDni = value => {
  const dni = normalizeIdentityValue(value);
  return /^\d{7,8}$/.test(dni);
};

const isValidArgCuit = value => {
  const cuit = normalizeIdentityValue(value);
  if (!/^\d{11}$/.test(cuit)) {
    return false;
  }

  const prefix = cuit.slice(0, 2);
  const validPrefixes = ['20', '23', '24', '27', '30', '33', '34'];
  if (!validPrefixes.includes(prefix)) {
    return false;
  }

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((acc, weight, index) => {
    return acc + Number(cuit[index]) * weight;
  }, 0);

  let checkDigit = 11 - (sum % 11);
  if (checkDigit === 11) {
    checkDigit = 0;
  } else if (checkDigit === 10) {
    checkDigit = 9;
  }

  return checkDigit === Number(cuit[10]);
};

const isValidCpf = value => {
  const cpf = normalizeIdentityValue(value);
  return /^[0-9]{11}$/.test(cpf);
};

const isValidCnpj = value => {
  const cnpj = String(value ?? '').replace(/[^A-Za-z0-9]/g, '');
  return /^[A-Za-z0-9]{12}[0-9]{2}$/.test(cnpj);
};

export const getIdentityFieldForUserType = (userType, countryCode = COUNTRY_CODE) => {
  if (userType === CUSTOMER_USER_TYPE) {
    return CUSTOMER_IDENTITY_FIELD_BY_COUNTRY[countryCode] || 'dni';
  }

  if (userType === OFFICIAL_STORE_USER_TYPE) {
    return OFFICIAL_STORE_IDENTITY_FIELD_BY_COUNTRY[countryCode] || 'cuit';
  }

  return null;
};

export const getLegacyIdentityFieldForUserType = userType => {
  if (userType === CUSTOMER_USER_TYPE) {
    return 'dni';
  }

  if (userType === OFFICIAL_STORE_USER_TYPE) {
    return 'cuit';
  }

  return null;
};

export const isCustomerIdentityValid = (value, countryCode = COUNTRY_CODE) => {
  if (countryCode === 'BR') {
    return isValidCpf(value);
  }

  return isValidArgDni(value);
};

export const isOfficialStoreIdentityValid = (value, countryCode = COUNTRY_CODE) => {
  if (countryCode === 'BR') {
    return isValidCnpj(value);
  }

  return isValidArgCuit(value);
};

export const getIdentityMessageIds = (formName, userType) => {
  if (userType === CUSTOMER_USER_TYPE) {
    return {
      label: `${formName}.dniLabel`,
      placeholder: `${formName}.dniPlaceholder`,
      required: `${formName}.dniRequired`,
      invalid: `${formName}.dniInvalid`,
    };
  }

  if (userType === OFFICIAL_STORE_USER_TYPE) {
    return {
      label: `${formName}.cuitLabel`,
      placeholder: `${formName}.cuitPlaceholder`,
      required: `${formName}.cuitRequired`,
      invalid: `${formName}.cuitInvalid`,
    };
  }

  return null;
};

export const getSignupIdentityTakenErrorMessageId = (
  userType
) => {
  if (userType === CUSTOMER_USER_TYPE) {
    return 'AuthenticationPage.signupFailedDniAlreadyTaken';
  }

  if (userType === OFFICIAL_STORE_USER_TYPE) {
    return 'AuthenticationPage.signupFailedCuitAlreadyTaken';
  }

  return 'AuthenticationPage.signupFailed';
};
